import React, { useEffect, useState, useTransition } from "react";
import { apiClient } from "../../lib/api-client";
import { formatDateTimeSaoPaulo } from "../../lib/currency";

interface AdminEmailListItem {
  id: string;
  direction: "inbound" | "outbound";
  from_address: string;
  to_address: string;
  subject: string;
  body_text: string | null;
  status: string;
  read_at: string | null;
  created_at: string;
  attachment_count: number;
}

interface AdminEmailAttachment {
  id: string;
  email_id: string;
  filename: string;
  content_type: string;
  size_bytes: number;
  created_at: string;
}

interface AdminEmailDetail extends AdminEmailListItem {
  body_html: string | null;
  provider_message_id: string | null;
  attachments: AdminEmailAttachment[];
}

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export function AdminEmailsPage() {
  const [activeTab, setActiveTab] = useState<"inbound" | "outbound" | "compose">("inbound");
  const [emails, setEmails] = useState<AdminEmailListItem[]>([]);
  const [unreadCount, setUnreadCount] = useState<number>(0);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [selectedEmail, setSelectedEmail] = useState<AdminEmailDetail | null>(null);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [, startTransition] = useTransition();

  // Estados do formulário de envio (Composer)
  const [composerTo, setComposerTo] = useState("");
  const [composerSubject, setComposerSubject] = useState("");
  const [composerBody, setComposerBody] = useState("");
  const [composerFiles, setComposerFiles] = useState<File[]>([]);
  const [sending, setSending] = useState(false);
  const [sendSuccessMessage, setSendSuccessMessage] = useState<string | null>(null);
  const [sendErrorMessage, setSendErrorMessage] = useState<string | null>(null);

  async function loadEmails(direction: "inbound" | "outbound" = activeTab === "compose" ? "inbound" : activeTab) {
    setLoading(true);
    try {
      const query = new URLSearchParams();
      query.set("direction", direction);
      if (search.trim()) {
        query.set("search", search.trim());
      }
      const data = await apiClient.get<{
        items: AdminEmailListItem[];
        unreadCount: number;
        total: number;
      }>(`/admin/emails?${query.toString()}`);

      setEmails(data.items);
      setUnreadCount(data.unreadCount);
    } catch (err: unknown) {
      console.error("Erro ao carregar e-mails:", err);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    if (activeTab !== "compose") {
      void loadEmails(activeTab);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  async function handleOpenEmail(emailId: string) {
    setLoadingDetail(true);
    try {
      const data = await apiClient.get<AdminEmailDetail>(`/admin/emails/${emailId}`);
      setSelectedEmail(data);

      // Atualiza estado local de leitura
      startTransition(() => {
        setEmails((prev) =>
          prev.map((e) => (e.id === emailId ? { ...e, read_at: new Date().toISOString() } : e)),
        );
        setUnreadCount((prev) => Math.max(0, prev - 1));
      });
    } catch (err: unknown) {
      alert("Falha ao abrir e-mail: " + (err instanceof Error ? err.message : String(err)));
    } finally {
      setLoadingDetail(false);
    }
  }

  async function handleDownloadAttachment(att: AdminEmailAttachment) {
    try {
      const token = typeof window !== "undefined" ? localStorage.getItem("datasci_admin_token") : null;
      const headers = new Headers();
      if (token) headers.set("Authorization", `Bearer ${token}`);

      const res = await fetch(`/api/admin/emails/attachments/${att.id}`, {
        headers,
        credentials: "include",
      });

      if (!res.ok) {
        throw new Error(`Falha no download (HTTP ${res.status})`);
      }

      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = att.filename;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (err: unknown) {
      alert("Erro ao baixar anexo: " + (err instanceof Error ? err.message : String(err)));
    }
  }

  function handleReply(email: AdminEmailDetail) {
    setComposerTo(email.direction === "inbound" ? email.from_address : email.to_address);
    setComposerSubject(
      email.subject.startsWith("Re:") ? email.subject : `Re: ${email.subject}`,
    );
    setComposerBody(
      `\n\n--- Mensagem original de ${email.from_address} em ${formatDateTimeSaoPaulo(email.created_at)} ---\n${email.body_text || ""}`,
    );
    setSelectedEmail(null);
    setActiveTab("compose");
  }

  function handleFileChange(e: React.ChangeEvent<HTMLInputElement>) {
    if (e.target.files) {
      const newFiles = Array.from(e.target.files);
      setComposerFiles((prev) => [...prev, ...newFiles]);
    }
  }

  function removeFile(index: number) {
    setComposerFiles((prev) => prev.filter((_, i) => i !== index));
  }

  async function handleSendEmail(e: React.FormEvent) {
    e.preventDefault();
    setSending(true);
    setSendSuccessMessage(null);
    setSendErrorMessage(null);

    try {
      const formData = new FormData();
      formData.append("to", composerTo.trim());
      formData.append("subject", composerSubject.trim());
      formData.append("body", composerBody);
      formData.append("isHtml", "false");

      for (const file of composerFiles) {
        formData.append("files", file);
      }

      await apiClient.postForm("/admin/emails/send", formData);

      setSendSuccessMessage("E-mail despachado com sucesso via Resend!");
      setComposerTo("");
      setComposerSubject("");
      setComposerBody("");
      setComposerFiles([]);

      // Atualiza lista em segundo plano
      setTimeout(() => {
        setActiveTab("outbound");
      }, 1200);
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : String(err);
      setSendErrorMessage(`Erro no envio: ${msg}`);
    } finally {
      setSending(false);
    }
  }

  return (
    <div>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          flexWrap: "wrap",
          gap: "var(--space-2)",
          marginBottom: "var(--space-3)",
        }}
      >
        <div>
          <h2 style={{ margin: 0 }}>E-mails & Webmail Corporativo</h2>
          <div className="text-muted" style={{ fontSize: "var(--text-sm)", marginTop: "4px" }}>
            Envio e recebimento pelo domínio <strong>datasci.com.br</strong> (Resend + Cloudflare D1/R2)
          </div>
        </div>

        <div style={{ display: "flex", gap: "var(--space-2)" }}>
          <button
            className={`btn ${activeTab === "compose" ? "btn-primary" : "btn-secondary"}`}
            onClick={() => {
              setSelectedEmail(null);
              setActiveTab("compose");
            }}
          >
            ✉️ Escrever E-mail
          </button>
        </div>
      </div>

      {/* Tabs de navegação */}
      <div
        style={{
          display: "flex",
          borderBottom: "1px solid var(--border-color, #e5e7eb)",
          marginBottom: "var(--space-3)",
          gap: "var(--space-2)",
        }}
      >
        <button
          onClick={() => {
            setSelectedEmail(null);
            setActiveTab("inbound");
          }}
          style={{
            padding: "8px 16px",
            border: "none",
            background: "none",
            cursor: "pointer",
            fontWeight: activeTab === "inbound" ? 600 : 400,
            borderBottom: activeTab === "inbound" ? "2px solid var(--brand-primary, #0284c7)" : "none",
            color: activeTab === "inbound" ? "var(--brand-primary, #0284c7)" : "inherit",
          }}
        >
          📥 Caixa de Entrada {unreadCount > 0 && <span className="status-badge status-badge--paid" style={{ marginLeft: "6px" }}>{unreadCount}</span>}
        </button>

        <button
          onClick={() => {
            setSelectedEmail(null);
            setActiveTab("outbound");
          }}
          style={{
            padding: "8px 16px",
            border: "none",
            background: "none",
            cursor: "pointer",
            fontWeight: activeTab === "outbound" ? 600 : 400,
            borderBottom: activeTab === "outbound" ? "2px solid var(--brand-primary, #0284c7)" : "none",
            color: activeTab === "outbound" ? "var(--brand-primary, #0284c7)" : "inherit",
          }}
        >
          📤 Enviados
        </button>

        <button
          onClick={() => {
            setSelectedEmail(null);
            setActiveTab("compose");
          }}
          style={{
            padding: "8px 16px",
            border: "none",
            background: "none",
            cursor: "pointer",
            fontWeight: activeTab === "compose" ? 600 : 400,
            borderBottom: activeTab === "compose" ? "2px solid var(--brand-primary, #0284c7)" : "none",
            color: activeTab === "compose" ? "var(--brand-primary, #0284c7)" : "inherit",
          }}
        >
          ✏️ Novo E-mail
        </button>
      </div>

      {/* ABA: COMPOSER (ESCREVER E-MAIL) */}
      {activeTab === "compose" && (
        <div className="card" style={{ maxWidth: "800px", padding: "var(--space-4)" }}>
          <h3 style={{ marginTop: 0 }}>Novo E-mail Corporativo</h3>
          {sendSuccessMessage && (
            <div style={{ padding: "12px", background: "#ecfdf5", color: "#065f46", borderRadius: "6px", marginBottom: "16px" }}>
              ✓ {sendSuccessMessage}
            </div>
          )}
          {sendErrorMessage && (
            <div style={{ padding: "12px", background: "#fef2f2", color: "#991b1b", borderRadius: "6px", marginBottom: "16px" }}>
              ✕ {sendErrorMessage}
            </div>
          )}

          <form onSubmit={handleSendEmail}>
            <div style={{ marginBottom: "16px" }}>
              <label style={{ display: "block", fontSize: "14px", fontWeight: 500, marginBottom: "4px" }}>De</label>
              <input
                type="text"
                disabled
                value="Datasci Editora <contato@datasci.com.br>"
                className="input"
                style={{ width: "100%", background: "#f9fafb", cursor: "not-allowed" }}
              />
            </div>

            <div style={{ marginBottom: "16px" }}>
              <label style={{ display: "block", fontSize: "14px", fontWeight: 500, marginBottom: "4px" }}>Para *</label>
              <input
                type="email"
                required
                placeholder="destinatario@exemplo.com"
                value={composerTo}
                onChange={(e) => setComposerTo(e.target.value)}
                className="input"
                style={{ width: "100%" }}
              />
            </div>

            <div style={{ marginBottom: "16px" }}>
              <label style={{ display: "block", fontSize: "14px", fontWeight: 500, marginBottom: "4px" }}>Assunto *</label>
              <input
                type="text"
                required
                placeholder="Assunto do e-mail"
                value={composerSubject}
                onChange={(e) => setComposerSubject(e.target.value)}
                className="input"
                style={{ width: "100%" }}
              />
            </div>

            <div style={{ marginBottom: "16px" }}>
              <label style={{ display: "block", fontSize: "14px", fontWeight: 500, marginBottom: "4px" }}>Mensagem *</label>
              <textarea
                required
                rows={10}
                placeholder="Escreva sua mensagem aqui..."
                value={composerBody}
                onChange={(e) => setComposerBody(e.target.value)}
                className="input"
                style={{ width: "100%", fontFamily: "inherit", resize: "vertical" }}
              />
            </div>

            {/* Upload de Anexos */}
            <div style={{ marginBottom: "20px" }}>
              <label style={{ display: "block", fontSize: "14px", fontWeight: 500, marginBottom: "4px" }}>
                Anexos (PDF, imagens, documentos)
              </label>
              <input
                type="file"
                multiple
                onChange={handleFileChange}
                style={{ display: "block", marginBottom: "8px" }}
              />

              {composerFiles.length > 0 && (
                <div style={{ display: "flex", flexDirection: "column", gap: "6px", marginTop: "8px" }}>
                  {composerFiles.map((file, idx) => (
                    <div
                      key={idx}
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        background: "#f3f4f6",
                        padding: "6px 12px",
                        borderRadius: "4px",
                        fontSize: "13px",
                      }}
                    >
                      <span>
                        📎 <strong>{file.name}</strong> ({formatBytes(file.size)})
                      </span>
                      <button
                        type="button"
                        onClick={() => removeFile(idx)}
                        style={{
                          background: "none",
                          border: "none",
                          color: "#ef4444",
                          cursor: "pointer",
                          fontWeight: "bold",
                        }}
                      >
                        ✕ Remover
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div style={{ display: "flex", gap: "12px", alignItems: "center" }}>
              <button type="submit" className="btn btn-primary" disabled={sending}>
                {sending ? "Enviando e-mail..." : "🚀 Enviar E-mail"}
              </button>
              <button
                type="button"
                className="btn btn-secondary"
                disabled={sending}
                onClick={() => {
                  setComposerTo("");
                  setComposerSubject("");
                  setComposerBody("");
                  setComposerFiles([]);
                  setActiveTab("inbound");
                }}
              >
                Cancelar
              </button>
            </div>
          </form>
        </div>
      )}

      {/* ABA: LISTA DE E-MAILS (INBOUND / OUTBOUND) */}
      {activeTab !== "compose" && (
        <div style={{ display: "grid", gridTemplateColumns: selectedEmail ? "1fr 1.2fr" : "1fr", gap: "var(--space-3)" }}>
          <div className="card" style={{ padding: "var(--space-3)" }}>
            <div style={{ display: "flex", gap: "8px", marginBottom: "12px" }}>
              <input
                type="text"
                placeholder="Buscar por assunto ou e-mail..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") loadEmails(activeTab);
                }}
                className="input"
                style={{ flex: 1 }}
              />
              <button className="btn btn-secondary" onClick={() => loadEmails(activeTab)}>
                Buscar
              </button>
            </div>

            {loading ? (
              <div style={{ padding: "24px", textAlign: "center", color: "#6b7280" }}>Carregando e-mails...</div>
            ) : emails.length === 0 ? (
              <div style={{ padding: "32px", textAlign: "center", color: "#6b7280" }}>
                Nenhum e-mail encontrado {activeTab === "inbound" ? "na Caixa de Entrada" : "em Enviados"}.
              </div>
            ) : (
              <div style={{ overflowX: "auto" }}>
                <table className="data-table" style={{ width: "100%", fontSize: "14px" }}>
                  <thead>
                    <tr>
                      <th style={{ width: "24px" }}></th>
                      <th>{activeTab === "inbound" ? "De" : "Para"}</th>
                      <th>Assunto</th>
                      <th style={{ width: "40px" }}>📎</th>
                      <th>Data</th>
                      <th style={{ width: "70px" }}>Ação</th>
                    </tr>
                  </thead>
                  <tbody>
                    {emails.map((item) => {
                      const isUnread = item.direction === "inbound" && !item.read_at;
                      const isSelected = selectedEmail?.id === item.id;
                      return (
                        <tr
                          key={item.id}
                          style={{
                            backgroundColor: isSelected ? "#f0f9ff" : isUnread ? "#f8fafc" : "transparent",
                            fontWeight: isUnread ? 600 : 400,
                            cursor: "pointer",
                          }}
                          onClick={() => handleOpenEmail(item.id)}
                        >
                          <td>
                            {isUnread && (
                              <span
                                style={{
                                  display: "inline-block",
                                  width: "8px",
                                  height: "8px",
                                  borderRadius: "50%",
                                  backgroundColor: "#0284c7",
                                }}
                              />
                            )}
                          </td>
                          <td style={{ maxWidth: "160px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                            {activeTab === "inbound" ? item.from_address : item.to_address}
                          </td>
                          <td style={{ maxWidth: "220px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                            {item.subject}
                          </td>
                          <td style={{ textAlign: "center" }}>
                            {item.attachment_count > 0 ? `📎 ${item.attachment_count}` : ""}
                          </td>
                          <td style={{ whiteSpace: "nowrap", fontSize: "12px", color: "#6b7280" }}>
                            {formatDateTimeSaoPaulo(item.created_at)}
                          </td>
                          <td>
                            <button
                              className="btn btn-secondary"
                              style={{ fontSize: "12px", padding: "2px 8px" }}
                              onClick={(e) => {
                                e.stopPropagation();
                                handleOpenEmail(item.id);
                              }}
                            >
                              Abrir
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* PAINEL LEITOR DO E-MAIL SELECIONADO */}
          {loadingDetail && (
            <div className="card" style={{ padding: "var(--space-4)", textAlign: "center", color: "#6b7280" }}>
              Carregando mensagem...
            </div>
          )}
          {!loadingDetail && selectedEmail && (
            <div className="card" style={{ padding: "var(--space-4)", display: "flex", flexDirection: "column", height: "fit-content" }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: "16px" }}>
                <div>
                  <h3 style={{ margin: "0 0 8px 0", fontSize: "18px" }}>{selectedEmail.subject}</h3>
                  <div style={{ fontSize: "13px", color: "#4b5563" }}>
                    <div>
                      <strong>De:</strong> {selectedEmail.from_address}
                    </div>
                    <div>
                      <strong>Para:</strong> {selectedEmail.to_address}
                    </div>
                    <div>
                      <strong>Data:</strong> {formatDateTimeSaoPaulo(selectedEmail.created_at)}
                    </div>
                    <div>
                      <strong>Status:</strong>{" "}
                      <span className={`status-badge ${selectedEmail.status === "delivered" || selectedEmail.status === "received" ? "status-badge--paid" : "status-badge--negative"}`}>
                        {selectedEmail.status}
                      </span>
                    </div>
                  </div>
                </div>

                <div style={{ display: "flex", gap: "8px" }}>
                  <button className="btn btn-secondary" style={{ fontSize: "12px" }} onClick={() => handleReply(selectedEmail)}>
                    ↩ Responder
                  </button>
                  <button
                    className="btn btn-secondary"
                    style={{ fontSize: "12px" }}
                    onClick={() => setSelectedEmail(null)}
                  >
                    ✕ Fechar
                  </button>
                </div>
              </div>

              <div
                style={{
                  borderTop: "1px solid #e5e7eb",
                  borderBottom: "1px solid #e5e7eb",
                  padding: "16px 0",
                  minHeight: "150px",
                  whiteSpace: "pre-wrap",
                  fontFamily: "inherit",
                  lineHeight: 1.5,
                  fontSize: "14px",
                  color: "#1f2937",
                }}
              >
                {selectedEmail.body_text || (
                  <div dangerouslySetInnerHTML={{ __html: selectedEmail.body_html || "(Sem conteúdo)" }} />
                )}
              </div>

              {/* Anexos */}
              {selectedEmail.attachments && selectedEmail.attachments.length > 0 && (
                <div style={{ marginTop: "16px" }}>
                  <h4 style={{ margin: "0 0 8px 0", fontSize: "14px" }}>
                    Anexos ({selectedEmail.attachments.length})
                  </h4>
                  <div style={{ display: "flex", flexWrap: "wrap", gap: "8px" }}>
                    {selectedEmail.attachments.map((att) => (
                      <div
                        key={att.id}
                        style={{
                          border: "1px solid #e5e7eb",
                          borderRadius: "6px",
                          padding: "8px 12px",
                          display: "flex",
                          alignItems: "center",
                          gap: "8px",
                          background: "#f9fafb",
                          fontSize: "13px",
                        }}
                      >
                        <span>📄 {att.filename}</span>
                        <span style={{ color: "#6b7280", fontSize: "11px" }}>({formatBytes(att.size_bytes)})</span>
                        <button
                          className="btn btn-secondary"
                          style={{ fontSize: "11px", padding: "2px 6px", marginLeft: "4px" }}
                          onClick={() => handleDownloadAttachment(att)}
                        >
                          ⬇ Baixar
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
